import React, { useCallback, useEffect, useState } from 'react'
import { RootState } from 'redux/store'
import { useDispatch, useSelector } from 'react-redux'
import { PaymentMethod } from 'models/ChargeIntent'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import { Logo } from 'modules/ui/atoms/Logo'
import useFlow from 'modules/flow/state/use-flow'
import PaymentSelector from './components/payment-selector'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import { setPaymentProcessor, setPaymentType } from 'redux/features/paymentDetails'
import {
  getPaymentMethodConfig,
  useActivePaymentMethodConfigs,
} from './utils/use-payment-method-configs'
import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import styled from '@emotion/styled'
import { CLASSY_DESIGN_TOKENS } from '../../../../tokens'
import { DEFAULT_FONT_STYLES } from 'css/theme'
import PaymentSelectorSkeleton from './components/payment-selector-skeleton'
import AmountCurrencyFrequencySection from '../dcf-nudge-step/components/amount-currency-frequency-section'
import useOrder from 'modules/order/state/order/use-order'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { useTranslation } from 'next-i18next'
import { translate } from '../../../../utils/functions/translate'

// Styled component for the "Powered By" section
const PoweredByDiv = styled.div`
  text-align: center;
  color: ${CLASSY_DESIGN_TOKENS['--classy-color__neutral--30']};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  padding: 8px 0px;
`

// All transactions are secured notice (SSL)
const SslInfoDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${CLASSY_DESIGN_TOKENS['--classy-color__neutral--30']};
  font-size: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-size']};
  gap: 4px;
  line-height: 16px;
`

const PaymentMethodsStep = () => {
  const { t, i18n } = useTranslation()
  const flow = useFlow()
  const dispatch = useDispatch()
  const analytics = getAnalyticsInstance()
  const order = useOrder()

  const [isLoading, setIsLoading] = useState(true)

  // Selector to get the currently selected payment method
  const selectedPaymentMethod = useSelector((state: RootState) => state.order.paymentDetails.type)

  // Get the config for the selected payment method
  const selectedPaymentMethodConfig = getPaymentMethodConfig(selectedPaymentMethod, dispatch)

  // Pass canMakePayment to ensure recalculation
  const activePaymentMethodConfigs = useActivePaymentMethodConfigs()

  // Handle selection of payment methods
  const onSelect = useCallback(
    (value: PaymentMethod) => {
      const config = activePaymentMethodConfigs.find((config) => config.key === value)

      if (config && config.processor) {
        // Dispatch actions to set the payment type and processor
        dispatch(setPaymentType(value))
        dispatch(setPaymentProcessor(config.processor))

        // Track payment processor in analytics
        if (analytics && analytics.storage) {
          analytics.storage.setItem('paymentProcessor', config.processor)
        }
      } else {
        console.error(`No valid processor found for payment method: ${value}`)
      }
    },
    [activePaymentMethodConfigs, analytics, dispatch],
  )

  // Monitor the active payment methods and set loading state accordingly
  useEffect(() => {
    if (activePaymentMethodConfigs.length > 0) {
      setIsLoading(false)
    }
  }, [activePaymentMethodConfigs])

  // Ensure the selected payment method is selected after loading
  useEffect(() => {
    if (!isLoading && selectedPaymentMethod) {
      onSelect(selectedPaymentMethod)
    }
  }, [isLoading, onSelect, selectedPaymentMethod])

  const SelectedPaymentMethodActionButton = selectedPaymentMethodConfig?.actionButton

  const PaymentMethodFooterLabels: { [key: string]: string } = {
    card: translate('PaymentMethodsStep.footer.continue', 'Continue'),
    bank_transfer: translate('PaymentMethodsStep.footer.continue', 'Continue'),
  }

  return (
    <>
      <FlowTwoColumnStepLayout.Header
        title={flow.currentStep.title}
        backButtonDataId="payment-methods-step-back-button"
      />
      <FlowTwoColumnStepLayout.Body>
        <Column>
          <Row
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'end',
              paddingTop: '20px',
            }}
          >
            <AmountCurrencyFrequencySection
              amount={order.amount.value}
              currency={order.currency.value}
              frequency={order.frequency.value}
              locale={i18n.language}
            />
          </Row>
          <SslInfoDiv>
            <UiIcon w={24} h={24} name="shield" />
            {t(
              'PaymentMethodsStep.sslInfo.encryptedMessage',
              'All transactions are secure and encrypted',
            )}
          </SslInfoDiv>
          <Row>
            {isLoading ? (
              <PaymentSelectorSkeleton /> // Show loading skeleton while payment methods load
            ) : (
              <PaymentSelector
                onChange={onSelect}
                value={selectedPaymentMethod}
                paymentMethodConfigs={activePaymentMethodConfigs}
              />
            )}
          </Row>
        </Column>
        <PoweredByDiv>{t('PaymentMethodsStep.poweredBy', 'Powered by GoFundMe')}</PoweredByDiv>
      </FlowTwoColumnStepLayout.Body>
      <FlowTwoColumnStepLayout.Footer>
        {isLoading ? (
          // Show a dummy Continue button while payment methods are loading.
          <FlowTwoColumnStepLayout.FooterButton>
            {t('PaymentMethodsStep.footer.continue', 'Continue')}
          </FlowTwoColumnStepLayout.FooterButton>
        ) : (
          SelectedPaymentMethodActionButton && (
            <SelectedPaymentMethodActionButton
              dataId={`payment-method-continue-button-${selectedPaymentMethod.toLowerCase()}`}
            >
              {!selectedPaymentMethodConfig.actionButtonText && selectedPaymentMethodConfig.labelKey
                ? PaymentMethodFooterLabels[selectedPaymentMethodConfig.labelKey]
                : selectedPaymentMethodConfig.actionButtonText}
              {selectedPaymentMethodConfig?.actionButtonLogo && (
                <Logo
                  name={selectedPaymentMethodConfig?.actionButtonLogo}
                  altText={selectedPaymentMethodConfig?.actionButtonLogoAltText}
                />
              )}
            </SelectedPaymentMethodActionButton>
          )
        )}
      </FlowTwoColumnStepLayout.Footer>
    </>
  )
}

export default PaymentMethodsStep
